/**
 * S13 — Tab "Membros" do painel People Memory.
 * Lista membros do grupo com contagens por status e toggles de consent/dm_consent.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface MemberRow {
  id: string;
  member_jid: string;
  display_name: string | null;
  role: string;
  is_subject_consented: boolean;
  dm_consent: boolean;
  confirmed: number;
  pending: number;
  perception: number;
}

export function PeopleMemoryMembers({ groupId }: { groupId: string }) {
  const qc = useQueryClient();

  const { data: members = [], isLoading } = useQuery<MemberRow[]>({
    queryKey: ["pm-members", groupId],
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const { data: m } = await supabase
        .from("syra_group_members")
        .select("id, member_jid, display_name, role, is_subject_consented, dm_consent")
        .eq("group_id", groupId)
        .is("left_at", null);

      const list = (m ?? []) as any[];
      const jids = list.map((x) => x.member_jid);
      const factsResp = jids.length
        ? await supabase
          .from("syra_person_memories")
          .select("subject_jid, confirmation_status")
          .eq("group_id", groupId)
          .in("subject_jid", jids)
        : { data: [] as any[] };

      const counts: Record<string, { confirmed: number; pending: number; perception: number }> = {};
      for (const f of (factsResp.data ?? []) as any[]) {
        const c = counts[f.subject_jid] ??= { confirmed: 0, pending: 0, perception: 0 };
        if (["confirmed_by_subject", "confirmed_by_admin", "self_claim"].includes(f.confirmation_status)) c.confirmed++;
        else if (["candidate", "awaiting_confirmation"].includes(f.confirmation_status)) c.pending++;
        else if (f.confirmation_status === "social_perception") c.perception++;
      }

      return list.map((row) => ({
        ...row,
        confirmed: counts[row.member_jid]?.confirmed ?? 0,
        pending: counts[row.member_jid]?.pending ?? 0,
        perception: counts[row.member_jid]?.perception ?? 0,
      }));
    },
  });

  const toggle = async (id: string, field: "is_subject_consented" | "dm_consent", value: boolean) => {
    const payload: any = { [field]: value };
    const { error } = await supabase.from("syra_group_members").update(payload).eq("id", id);
    if (error) toast.error(error.message);
    else {
      toast.success("Atualizado");
      qc.invalidateQueries({ queryKey: ["pm-members", groupId] });
    }
  };

  if (isLoading) return <div className="text-sm text-muted-foreground p-4">Carregando…</div>;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nome</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Confirmados</TableHead>
          <TableHead>Pendentes</TableHead>
          <TableHead>Percepção</TableHead>
          <TableHead>Consent</TableHead>
          <TableHead>DM Consent</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((m) => (
          <TableRow key={m.id}>
            <TableCell className="font-medium">{m.display_name || m.member_jid}</TableCell>
            <TableCell><Badge variant="outline">{m.role}</Badge></TableCell>
            <TableCell><Badge>{m.confirmed}</Badge></TableCell>
            <TableCell><Badge variant="secondary">{m.pending}</Badge></TableCell>
            <TableCell><Badge variant="outline">{m.perception}</Badge></TableCell>
            <TableCell>
              <Switch
                checked={m.is_subject_consented}
                onCheckedChange={(v) => toggle(m.id, "is_subject_consented", v)}
              />
            </TableCell>
            <TableCell>
              <Switch
                checked={m.dm_consent}
                onCheckedChange={(v) => toggle(m.id, "dm_consent", v)}
              />
            </TableCell>
          </TableRow>
        ))}
        {members.length === 0 && (
          <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Sem membros cadastrados.</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}
